import React, { useState } from "react";
import { connect } from "react-redux";
import { motion } from "framer-motion";
import { X, CheckCircle, Sparkles } from "lucide-react";
import ModalOverlay from "../ModalOverlay/ModalOverlay.tsx";
import {
  activateFreeTrial,
  closeSuscriptionModal,
  setActivateFreeTrial,
} from "../../../store/actions/profileActions";
import { loadUserData, upgradeJobboardToClientAndActivateTrial } from "../../../store/actions";
import { shouldShowJobboardUpgradePrompt } from "../../../utils/jobboard/jobboardProfileGate.ts";
import {
  formatFreeTrialDaysActivated,
  formatFreeTrialDaysLabel,
  getFreeTrialDays,
  getFreeTrialEarlyAdopterDays,
} from "../../../utils/freeTrial.ts";
import styles from "./FreeTrialModal.module.scss";

const modalVariants = {
  hidden: { opacity: 0, scale: 1.1, y: 20 },
  visible: {
    opacity: 1,
    scale: 1.0,
    y: 0,
    transition: { delay: 0.1, duration: 0.3, ease: "easeOut" },
  },
};

const BENEFITS = [
  "Interactuar con la IA de Edify de forma ilimitada",
  "Publicar productos/servicios en la página principal",
  "Crear proyectos de Obra Privada y obtener cotizaciones",
  "Postularte en publicaciones de otros usuarios y enviar presupuestos",
  "Acceder a toda la información en Obras Públicas",
  "Crear y cotizar solicitudes de Listado de Materiales",
  "Dar y recibir calificaciones a todos los usuarios",
  "Atención personalizada para consultas",
  "Mailing de difusión a base de datos (una vez al mes)",
];

type UpgradeJobboardResult = {
  success?: boolean;
  errorMessage?: string;
};

interface FreeTrialModalProps {
  closeSuscriptionModal: () => void;
  activateFreeTrial: () => Promise<unknown>;
  loadUserData: () => void;
  setActivateFreeTrial: (value: boolean) => void;
  upgradeJobboardToClientAndActivateTrial: (options?: {
    useMainModalFeedback?: boolean;
  }) => Promise<UpgradeJobboardResult | void>;
  userData: {
    free_trial?: {
      early_adopter?: boolean;
    };
  };
  isFreeTrialSuccess: boolean;
}

const FreeTrialModal: React.FC<FreeTrialModalProps> = ({
  closeSuscriptionModal,
  activateFreeTrial,
  upgradeJobboardToClientAndActivateTrial,
  loadUserData,
  setActivateFreeTrial,
  userData,
  isFreeTrialSuccess,
}) => {
  const [isActivating, setIsActivating] = useState(false);
  const [activationError, setActivationError] = useState<string | null>(null);

  const isJobboardFlow = shouldShowJobboardUpgradePrompt(userData);
  const isEarlyAdopter = userData?.free_trial?.early_adopter ?? false;
  const freeTrialDays = getFreeTrialDays();
  const earlyAdopterDays = getFreeTrialEarlyAdopterDays();
  const activatedDaysLabel = isEarlyAdopter
    ? formatFreeTrialDaysActivated(earlyAdopterDays)
    : formatFreeTrialDaysActivated(freeTrialDays);

  const onStartFreeTrial = () => {
    if (isActivating) return;

    setIsActivating(true);
    setActivationError(null);
    setActivateFreeTrial(false);

    const finishActivation = async () => {
      try {
        if (isJobboardFlow) {
          const result = (await upgradeJobboardToClientAndActivateTrial({
            useMainModalFeedback: false,
          })) as UpgradeJobboardResult | void;
          if (result?.success) {
            setActivateFreeTrial(true);
          } else {
            setActivationError(
              result?.errorMessage ??
                "No pudimos activar tu período de prueba. Intentá de nuevo en unos minutos."
            );
          }
          return;
        }

        await activateFreeTrial();
        setActivateFreeTrial(true);
      } catch {
        setActivationError(
          "No pudimos activar tu período de prueba. Intentá de nuevo en unos minutos."
        );
      } finally {
        setIsActivating(false);
      }
    };

    void finishActivation();
  };

  const handleClose = () => {
    if (isActivating) return;

    if (isFreeTrialSuccess) {
      loadUserData();
    }
    setActivateFreeTrial(false);
    closeSuscriptionModal();
  };

  const showSuccess = isFreeTrialSuccess && !isActivating;
  const showActivating = isActivating;
  const showIntro = !showSuccess && !showActivating;

  return (
    <ModalOverlay
      isOpen={true}
      onClose={isActivating ? undefined : handleClose}
      preventClose={isActivating}
    >
      <motion.div
        className={styles.modal}
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        <div className={styles.modalBody}>
          {!isActivating && (
            <button
              className={styles.closeButton}
              onClick={handleClose}
              aria-label="Cerrar modal"
              type="button"
            >
              <X size={20} />
            </button>
          )}

          {showActivating && (
            <div
              className={styles.activatingPanel}
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              <span className={styles.activatingSpinner} aria-hidden />
              <h3 className={styles.title}>
                {isJobboardFlow
                  ? "Actualizando tu cuenta"
                  : "Activando tu período de prueba"}
              </h3>
              <p className={styles.bodyText}>
                {isJobboardFlow
                  ? "Estamos pasando tu cuenta a miembro completo y activando el free trial. Esto puede tardar unos segundos."
                  : "Esperá un momento mientras activamos tu acceso premium."}
              </p>
            </div>
          )}

          {!showActivating && (
            <>
              <div className={styles.content}>
                <div className={styles.iconContainer}>
                  {showSuccess ? (
                    <CheckCircle size={48} className={styles.successIcon} />
                  ) : (
                    <Sparkles size={48} className={styles.icon} />
                  )}
                </div>

                {showSuccess ? (
                  <>
                    <h3 className={styles.title}>{activatedDaysLabel}</h3>
                    <p className={styles.bodyText}>
                      Obtienes los siguientes beneficios
                    </p>
                    <ul className={styles.benefitsList}>
                      {BENEFITS.map((benefit, index) => (
                        <li key={index}>{benefit}</li>
                      ))}
                    </ul>
                    <p className={styles.importantNote}>
                      <span className={styles.important}>¡Importante!</span> No se
                      necesita tarjeta de crédito
                    </p>
                    <p className={styles.disclaimer}>
                      Una vez finalizado el período de prueba el plan se cancela
                      automáticamente. Vas a poder ingresar y chequear nuevas
                      funcionalidades pero no vas a poder interactuar con los demás
                      usuarios.
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className={styles.title}>Contenido Premium</h3>
                    <p className={styles.bodyText}>
                      {isEarlyAdopter
                        ? "Muchas gracias por ser uno de los usuarios iniciales de Edify. Lamentablemente el tiempo de uso libre ha terminado. Debés suscribirte para acceder al contenido."
                        : `Comienza tus ${formatFreeTrialDaysLabel(freeTrialDays)} de prueba gratis y accedé a los siguientes beneficios:`}
                    </p>
                    {isEarlyAdopter && (
                      <p className={styles.earlyAdopterNote}>
                        Además, por ser un usuario inicial tenés el beneficio de{" "}
                        <strong>
                          {formatFreeTrialDaysLabel(earlyAdopterDays)} gratis
                        </strong>{" "}
                        por única vez.
                      </p>
                    )}
                    {activationError && (
                      <p className={styles.activationError}>{activationError}</p>
                    )}
                    <ul className={styles.benefitsList}>
                      {BENEFITS.map((benefit, index) => (
                        <li key={index}>{benefit}</li>
                      ))}
                    </ul>
                    <p className={styles.importantNote}>
                      <span className={styles.important}>¡Importante!</span> No se
                      necesita tarjeta de crédito
                    </p>
                    <p className={styles.disclaimer}>
                      Una vez finalizado el período de prueba el plan se cancela
                      automáticamente. Vas a poder ingresar y chequear nuevas
                      funcionalidades pero no vas a poder interactuar con los demás
                      usuarios.
                    </p>
                  </>
                )}
              </div>

              <div className={styles.options}>
                {showSuccess ? (
                  <button
                    className={styles.primaryButton}
                    onClick={handleClose}
                    type="button"
                  >
                    ¡Bienvenido a Edify Premium!
                  </button>
                ) : (
                  <>
                    <button
                      className={styles.primaryButton}
                      onClick={onStartFreeTrial}
                      type="button"
                      disabled={isActivating}
                    >
                      Comenzar Free Trial
                    </button>
                    <button
                      className={styles.secondaryButton}
                      onClick={handleClose}
                      type="button"
                      disabled={isActivating}
                    >
                      Cerrar
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </motion.div>
    </ModalOverlay>
  );
};

const mapStateToProps = (state: {
  general: { userData: FreeTrialModalProps["userData"] };
  profile: { isFreeTrialSuccess: boolean };
}) => ({
  userData: state.general.userData,
  isFreeTrialSuccess: state.profile.isFreeTrialSuccess,
});

export default connect(mapStateToProps, {
  closeSuscriptionModal,
  activateFreeTrial,
  upgradeJobboardToClientAndActivateTrial,
  loadUserData,
  setActivateFreeTrial,
})(FreeTrialModal);
